import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Controller,
  FAQIcon,
  FieldWrapper,
  Hint,
  Inscription,
  Label,
  RadioButton,
  SimpleHint,
  Text,
  useFormContext,
  useResponsive,
} from '@common-fe/common-fe';

import { useGetVerificationEmail, useIsCardVerificationAvailable } from './hooks';
import { LimitedAccessVerificationMethod } from './LimitedAccessVerification.types';
interface Props {}
const LimitedAccessVerificationMethodForm: React.FC<Props> = () => {
  const { isMobile } = useResponsive();
  const preferredEmail = useGetVerificationEmail();
  const isCardVerificationAvailable = useIsCardVerificationAvailable();

  const { t } = useTranslation();
  const {
    control,
    // formState: { errors },
  } = useFormContext<{
    method: LimitedAccessVerificationMethod;
  }>();
  return (
    <Box
      data-testid="limited-access-verification-method-form"
      pad={isMobile ? 'spacing8' : 'spacing24'}
      margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
      round="moduleRound"
      background="module"
    >
      <Box
        pad={isMobile ? 'spacing12' : 'spacing24'}
        round="container1Round"
        background="canvas"
        direction="column"
      >
        <FieldWrapper direction={isMobile ? 'column' : 'row'} align="start" height={{ min: 'auto' }}>
          <Label name="method" showRequireIcon isNarrow margin={isMobile ? { bottom: 'spacing4' } : {}}>
            {t('Verification method')}
          </Label>
          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <Box gap="spacing12" style={{ position: 'relative' }}>
                {preferredEmail && (
                  <RadioButton
                    data-testid="limited-access-verification-method-form_email-radio"
                    name="method"
                    inputStyle={
                      isMobile
                        ? {
                            alignItems: 'center',
                          }
                        : {}
                    }
                    onChange={() => field.onChange(LimitedAccessVerificationMethod.Email)}
                    label={
                      <Box fill direction="row" align="center">
                        <Box>
                          <Text size="small" margin={{ vertical: 'auto' }}>
                            {t('Send code to your employer-provided email - ')}{' '}
                            <Text size="small" weight={700}>
                              {preferredEmail}
                            </Text>
                          </Text>
                        </Box>
                        <div>
                          <Box margin={{ left: 'spacing8' }}>
                            <SimpleHint
                              testId="limited-access-verification-method-form_email-radio_hint"
                              style={{ zIndex: 10000 }}
                              content={
                                <Box direction="column" style={{ zIndex: 1000000 }}>
                                  <Text size="small">
                                    {t("Why can't I see the email address I provided during registration?")}
                                  </Text>

                                  <Text size="small" margin={{ top: 'spacing12' }}>
                                    {t(
                                      'For your security we can only verify your identity using the email address provided by your employer. If this email is not correct, contact your employer or plan administrator.'
                                    )}
                                  </Text>
                                </Box>
                              }
                            >
                              <FAQIcon size="20px" color="iconSecondary" />
                            </SimpleHint>
                          </Box>
                        </div>
                      </Box>
                    }
                    checked={field.value === LimitedAccessVerificationMethod.Email}
                  />
                )}
                <Hint
                  testId="limited-access-verification-method-form_email-radio_hint"
                  style={{ zIndex: 100 }}
                  contentStyles={{
                    marginLeft: '-166px',
                  }}
                  hideHint={isCardVerificationAvailable}
                  hintElement={
                    <RadioButton
                      data-testid="limited-access-verification-method-form_card-radio"
                      name="method"
                      disabled={!isCardVerificationAvailable}
                      onChange={() => field.onChange(LimitedAccessVerificationMethod.CardCode)}
                      label={
                        <Box height="24px" align="center">
                          <Text size="small" margin={{ vertical: 'auto' }}>
                            {t('Enter the last 4 digits of your payment card')}
                          </Text>
                        </Box>
                      }
                      checked={field.value === LimitedAccessVerificationMethod.CardCode}
                    />
                  }
                >
                  <Box direction="column" style={{ zIndex: 1000000 }}>
                    <Inscription size="small" textAlign="center">
                      {t(
                        'The card verification button will become enabled as soon as your card is created and sent to you.'
                      )}
                    </Inscription>
                  </Box>
                </Hint>
              </Box>
            )}
          />
        </FieldWrapper>
      </Box>
    </Box>
  );
};

export default LimitedAccessVerificationMethodForm;
